import { tokenStore, type StoredAuth } from './tokenStore'

export type Role = 'VIEWER' | 'EDITOR'

export type Permission =
  | 'read'
  | 'download'
  | 'upload'
  | 'delete'
  | 'delete-directory'
  | 'rename'
  | 'share'
  | 'publish'
  | 'admin'

export interface User {
  /** Account name. */
  username: string
  displayName: string
  permissions: Permission[]
  buckets: string[] | null
}

export interface Bucket {
  name: string
  createdAt: string | null
}

export interface FolderRow {
  prefix: string
  name: string
}

export interface ObjectRow {
  key: string
  name: string
  size: number
  lastModified: string
  etag: string
}

export interface ObjectMeta {
  key: string
  size: number
  lastModified: string
  etag: string
  contentType: string
  retentionUntil: string | null
  retentionHold: boolean
  locked: boolean
  lockReason: string | null
}

export interface ListResponse {
  bucket: string
  prefix: string
  folders: FolderRow[]
  objects: ObjectRow[]
  nextToken: string | null
}

export interface DeleteOutcome {
  key: string
  ok: boolean
  code: string | null
  message: string | null
}

export interface DeleteResponse {
  results: DeleteOutcome[]
  deleted: number
  blocked: number
  failed: number
}

export interface PreflightFile {
  name: string
  key: string
  exists: boolean
  locked: boolean
  lockReason: string | null
  existingSize: number | null
  existingModified: string | null
}

export interface CreateUploadResponse {
  id: string
  key: string
  bucket: string
  partSize: number
  totalParts: number
  replaceExisting: boolean
}

export interface ShareLink {
  id: string
  url: string
  token: string
  bucket: string
  key: string
  label: string | null
  createdBy: string
  createdAt: string
  expiresAt: string | null
  maxDownloads: number | null
  downloadCount: number
  hasPassword: boolean
  revokedAt: string | null
  lastAccessAt: string | null
  active: boolean
  problem: string | null
}

export interface PublicShare {
  available: boolean
  problem: string | null
  filename: string | null
  size: number | null
  requiresPassword: boolean
  label?: string | null
  expiresAt?: string | null
  downloadsRemaining?: number | null
}

export const ALL_PERMISSIONS: Permission[] = [
  'read',
  'download',
  'upload',
  'delete',
  'delete-directory',
  'rename',
  'share',
  'publish',
  'admin',
]

/** What each permission actually allows, shown beside the checkbox. */
export const PERMISSION_HELP: Record<Permission, string> = {
  read: 'List folders and read file details',
  download: 'Download file contents',
  upload: 'Upload new files',
  delete: 'Delete files',
  'delete-directory': 'Remove empty folders (deletes no file data)',
  rename: 'Rename files (copies, then deletes the original)',
  share: 'Create and revoke share links',
  publish: 'Make files publicly downloadable from HCP',
  admin: 'Create and revoke API keys',
}

export interface ApiKeySummary {
  id: string
  name: string
  hint: string
  permissions: Permission[]
  buckets: string[]
  disabled: boolean
  createdAt: string
  createdBy: string
  expiresAt: string | null
  lastUsedAt: string | null
  active: boolean
}

export interface CreatedApiKey {
  id: string
  name: string
  permissions: Permission[]
  buckets: string[]
  hint: string
  expiresAt: string | null
  /** Returned once, at creation, and never again. */
  key: string
  warning: string
}

export interface AuditEntry {
  id: string
  at: string
  username: string
  action: string
  bucket: string | null
  key: string | null
  bytes: number | null
  result: string
  detail: string | null
}

/** An error carrying the server's machine-readable code alongside its message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the operation was refused by an HCP retention period or hold. */
  get isRetentionBlocked(): boolean {
    return this.code === 'RETENTION_BLOCKED'
  }
}

/**
 * Refreshes the access token if it is close to expiry.
 *
 * The server holds no session, so keeping the token alive is this client's
 * job. Guarded by a shared promise so concurrent requests trigger one refresh
 * rather than a burst of them.
 */
let refreshInFlight: Promise<void> | null = null

async function ensureFreshToken(): Promise<void> {
  if (!tokenStore.needsRefresh() || tokenStore.isDead()) return
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const auth = tokenStore.read()
    if (!auth) return
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: auth.refreshToken }),
      })
      if (!res.ok) {
        tokenStore.clear()
        return
      }
      const next = (await res.json()) as Omit<StoredAuth, 'user'>
      tokenStore.write({ ...auth, ...next })
    } catch {
      // Leave the existing token in place; the request will fail with 401 and
      // the UI will ask for a fresh sign-in.
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/** Bearer header carrying the access token, when one is held. */
export function authHeader(): Record<string, string> {
  const token = tokenStore.accessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  await ensureFreshToken()

  const response = await fetch(path, {
    ...init,
      credentials: 'same-origin',
    headers: {
      ...(init.body && !(init.body instanceof Blob) ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
      ...init.headers,
    },
  })

  if (!response.ok) {
    let code = 'ERROR'
    let message = `Request failed (${response.status})`
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } }
      if (body.error?.code) code = body.error.code
      if (body.error?.message) message = body.error.message
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new ApiError(response.status, code, message)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const query = (params: Record<string, string | number | null | undefined>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value))
  }
  const str = search.toString()
  return str ? `?${str}` : ''
}

export const api = {
  // --- auth ---
  me: () => request<{ user: User }>('/api/auth/me'),

  login: (username: string, password: string) =>
    request<StoredAuth>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  // --- buckets ---
  buckets: () => request<{ driver: string; buckets: Bucket[] }>('/api/buckets'),

  // --- objects ---
  list: (bucket: string, prefix: string, token?: string | null) =>
    request<ListResponse>(`/api/objects${query({ bucket, prefix, token })}`),

  meta: (bucket: string, key: string) =>
    request<{ meta: ObjectMeta }>(`/api/objects/meta${query({ bucket, key })}`),

  /**
   * Retention state for a page of keys.
   *
   * Deliberately separate from `list`: S3 listings do not carry HCP's
   * X-HCP-* headers, so lock status needs a HEAD per object. Fetching it after
   * the rows render keeps the table fast.
   */
  metaBatch: (bucket: string, keys: string[]) =>
    request<{ metas: ObjectMeta[] }>('/api/objects/meta-batch', {
      method: 'POST',
      body: JSON.stringify({ bucket, keys }),
    }),

  // Downloads happen via top-level navigation, which cannot carry an
  // Authorization header — hence ?access_token=.
  downloadUrl: (bucket: string, key: string) =>
    `/api/objects/download${query({ bucket, key, access_token: tokenStore.accessToken() })}`,

  deleteObjects: (bucket: string, keys: string[]) =>
    request<DeleteResponse>('/api/objects/delete', {
      method: 'POST',
      body: JSON.stringify({ bucket, keys }),
    }),

  /**
   * Removes an empty directory.
   *
   * Separate from deleteObjects because HCP keeps a real directory tree:
   * emptying a folder leaves the folder itself behind, and removing it is not
   * an S3 operation at all. Fails with 409 if anything is still inside.
   */
  deleteDirectory: (bucket: string, path: string) =>
    request<{ path: string; deleted: boolean }>('/api/objects/directory/delete', {
      method: 'POST',
      body: JSON.stringify({ bucket, path }),
    }),

  // --- uploads ---
  preflight: (bucket: string, prefix: string, files: { name: string; size: number }[]) =>
    request<{ bucket: string; prefix: string; files: PreflightFile[] }>('/api/uploads/preflight', {
      method: 'POST',
      body: JSON.stringify({ bucket, prefix, files }),
    }),

  createUpload: (input: {
    bucket: string
    key: string
    size: number
    contentType: string
    onConflict: 'fail' | 'replace' | 'rename'
    /** Raw X-HCP-RETENTION value; omitted means no retention. */
    retention?: string | undefined
    /** Sets the object ACL to public-read once the upload completes. */
    makePublic?: boolean
  }) =>
    request<CreateUploadResponse>('/api/uploads', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Part bodies are sent with XMLHttpRequest rather than fetch — see
  // features/upload/uploadPart.ts — because fetch cannot report upload
  // progress, and a 2 GB transfer without a moving progress bar is
  // indistinguishable from a hang.

  getUpload: (uploadId: string) =>
    request<{
      id: string
      bucket: string
      key: string
      status: string
      partSize: number
      totalParts: number
      size: number
      uploadedParts: number[]
    }>(`/api/uploads/${uploadId}`),

  completeUpload: (uploadId: string) =>
    request<{
      key: string
      bucket: string
      size: number
      publicUrl: string | null
      publicError: string | null
    }>(`/api/uploads/${uploadId}/complete`, { method: 'POST' }),

  /**
   * Renames a file in place. `newName` is a filename, not a path — the folder
   * does not change.
   */
  renameObject: (bucket: string, key: string, newName: string) =>
    request<{ key: string; renamed: boolean; previousKey?: string; message?: string }>(
      '/api/objects/rename',
      { method: 'POST', body: JSON.stringify({ bucket, key, newName }) },
    ),

  // --- permanent public URLs (HCP object ACL) ---
  objectPublic: (bucket: string, key: string) =>
    request<{ isPublic: boolean; url: string | null; potentialUrl: string | null }>(
      `/api/objects/public${query({ bucket, key })}`,
    ),

  setObjectPublic: (bucket: string, key: string, isPublic: boolean) =>
    request<{ isPublic: boolean; url: string | null }>('/api/objects/public', {
      method: 'PUT',
      body: JSON.stringify({ bucket, key, isPublic }),
    }),

  abortUpload: (uploadId: string) =>
    request<{ ok: boolean }>(`/api/uploads/${uploadId}`, { method: 'DELETE' }),

  // --- share links ---
  createShare: (input: {
    bucket: string
    key: string
    expiresInHours: number | null
    maxDownloads: number | null
    password?: string
    label?: string
  }) =>
    request<{ share: ShareLink }>('/api/shares', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  shares: (params: { bucket?: string; key?: string; includeInactive?: boolean }) =>
    request<{ shares: ShareLink[] }>(
      `/api/shares${query({
        bucket: params.bucket,
        key: params.key,
        includeInactive: params.includeInactive ? 'true' : undefined,
      })}`,
    ),

  revokeShare: (id: string) => request<{ ok: boolean }>(`/api/shares/${id}`, { method: 'DELETE' }),

  // Public — no session required.
  publicShare: (token: string) => request<PublicShare>(`/api/public/shares/${token}`),

  publicDownloadUrl: (token: string, password?: string) =>
    `/api/public/shares/${token}/download${password ? query({ password }) : ''}`,

  // --- API keys ---
  apiKeys: () => request<{ keys: ApiKeySummary[] }>('/api/keys'),

  createApiKey: (input: {
    name: string
    permissions: Permission[]
    buckets: string[]
    expiresInDays?: number | null
  }) => request<CreatedApiKey>('/api/keys', { method: 'POST', body: JSON.stringify(input) }),

  revokeApiKey: (id: string) => request<{ ok: boolean }>(`/api/keys/${id}`, { method: 'DELETE' }),

  // --- audit ---
  audit: (params: {
    limit?: number
    cursor?: string | null
    username?: string
    action?: string
    bucket?: string
    result?: string
    q?: string
  }) => request<{ entries: AuditEntry[]; nextCursor: string | null }>(`/api/audit${query(params)}`),

  auditFacets: () => request<{ usernames: string[]; actions: string[] }>('/api/audit/facets'),
}
